import { Particle } from "./Particle";
import { BrushHair } from "./BrushHair";

const COLOR = "#ffffff";
const SCALE = window.devicePixelRatio || 1;
const BALL_WIDTH = 30
const MAX_PARTICLES = 100;
const PARTICLE_RADIUS = 15;
const PLAYBACK_DRAW_STEPS = 20;

class PlaybackHighlight {
  constructor(container, shapes) {
    this.container = container;
    this.colors = ['#ffffff'];
    this.canvas = document.createElement("canvas");
    this.canvas.classList.add("playback-highlight");
    this.container.appendChild(this.canvas);
    this.canvasContext = this.canvas.getContext("2d");
    this.shapes = shapes;
    this.stepCount = PLAYBACK_DRAW_STEPS;
    this.canvasCenter = { x: 0, y: 0 };
    this.particles = [];
    this.pool = [];
    this.hair = new BrushHair({
      length: 10,
      thickness: 1,
    });
    this.hair.thickness = 3;
    this.hair.roundMode = true;
  }

  updateColors(colors) {
    this.colors = colors;
  }

  spawnParticule(x, y, color, radius = PARTICLE_RADIUS) {
    if (this.particles.length >= MAX_PARTICLES) {
      this.pool.push(this.particles.shift());
    }

    const particle = this.pool.length ? this.pool.pop() : new Particle();
    particle.init(x, y, 8 + Math.random() * radius);

    particle.wander = 0.5 + Math.random() * 1.5;
    if (Math.random() < 0.3) {
      particle.color = this.colors[
        Math.round(Math.random() * this.colors.length - 1)
      ];
    } else {
      particle.color = color;
    }
    particle.drag = 0.9 + Math.random() * 0.09;

    const theta = Math.random() * Math.PI * 2;
    const force = 2 + Math.random() * 6;

    particle.vx = Math.sin(theta) * force;
    particle.vy = Math.cos(theta) * force;

    this.particles.push(particle);
  }

  resize(width, height) {
    this.width = width;
    this.height = height;
    this.canvas.width = this.width * SCALE;
    this.canvas.height = this.height * SCALE;
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    this.canvasCenter = { x: this.canvas.width / 2, y: this.canvas.height / 2 };
  }

  clear() {
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  destroy() {
    this.container.removeChild(this.canvas);
  }

  hideBall() {
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  drawCircle(x, y, color) {
    this.canvasContext.beginPath();
    this.canvasContext.ellipse(x, y, BALL_WIDTH, BALL_WIDTH, 0, 0, Math.PI * 2);
    this.canvasContext.fillStyle = color;
    this.canvasContext.fill();
  }

  update(time) {
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasContext.globalCompositeOperation = "source-over";

    const strokeCount = this.shapes.strokes.length;
    const stepCount =
      strokeCount > 5 ? PLAYBACK_DRAW_STEPS / 2 : PLAYBACK_DRAW_STEPS;
    // strokes
    for (const stroke of this.shapes.strokes) {
      const points = stroke.points;
      const pointCount = points.length;
      const firstPTime = points[0].loopTime;
      const lastPTime = points[pointCount - 1].loopTime;
      if (stroke.playerReady && pointCount > 2 &&
        ((firstPTime <= time && time <= lastPTime) ||
          (lastPTime < firstPTime && (firstPTime <= time || lastPTime >= time)))
      ) {
        for (let i = 1; i < pointCount; i++) {
          if (points[i].loopTime >= time && points[i].loopTime - time < 60) {
            const point = points[i];
            const color = this.colors[point.instrument];
            const x = point.x + this.canvas.width / 2;
            const y = point.y + this.canvas.height / 2;
            const steps = Math.min(i, stepCount);
            const startIndex = i - steps;
            let j = 0;
            if (strokeCount < 3) {
              for (j = startIndex; j < i; j++) {
                // draw some hairs
                let h = 0;
                for (const ha of points[j + 1].hairs) {
                  this.hair.draw(
                    this.canvasContext,
                    ha.outer,
                    ha.inner,
                    point.color,
                    0.85 + Math.random() * 0.25
                  );
                  h++;
                  if (h > 2) {
                    break;
                  }
                }
              }
            }

            const topPoints = [];
            const bottomPoints = [];
            for (j = startIndex; j < i; j++) {
              // draw ribbon in one shape
              topPoints.push({
                x: points[j].p1x + this.canvasCenter.x,
                y: points[j].p1y + this.canvasCenter.y
              });
              bottomPoints.unshift({
                x: points[j].p2x + this.canvasCenter.x,
                y: points[j].p2y + this.canvasCenter.y
              });
            }
            const ribbonPoints = topPoints.concat(bottomPoints);
            this.canvasContext.fillStyle = COLOR;
            this.canvasContext.beginPath();
            this.canvasContext.moveTo(ribbonPoints[0].x, ribbonPoints[0].y);
            for (j = 1; j < ribbonPoints.length; j++) {
              this.canvasContext.lineTo(ribbonPoints[j].x, ribbonPoints[j].y);
            }
            this.canvasContext.closePath();
            this.canvasContext.fill();

            const max = 1 + Math.random() * 1;
            for (j = 0; j < max; j++) {
              this.spawnParticule(x, y, color);
            }
            break;
          }
        }
      }
    }

    // stamps
    for (const stamp of this.shapes.stamps) {
      if (stamp.loopTime >= time && stamp.loopTime - time < 200) {
        for (let j = 0; j < 3 + Math.random() * 1; j++) {
          this.spawnParticule(
            stamp.x + this.canvas.width / 2,
            stamp.y + this.canvas.height / 2,
            this.colors[stamp.instrument]
          );
        }
      }
    }

    // update and draw particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      if (particle.alive) {
        particle.move();
      } else {
        this.pool.push(this.particles.splice(i, 1)[0]);
      }
    }
    this.drawParticules();
  }

  drawParticules() {
    this.canvasContext.globalCompositeOperation = "lighter";
    for (var i = this.particles.length - 1; i >= 0; i--) {
      this.particles[i].draw(this.canvasContext);
    }
  }
}

export { PlaybackHighlight };